import React, { useState } from "react";
import { useApi } from "../hooks/useApi.ts";
import { SearchSelect } from "./SearchSelect.tsx";
import { LoadingOverlay } from "./LoadingPanel.tsx";

interface Project {
  id: number;
  name: string;
}

interface ParsedResult {
  taskGroupId: number;
  parsed: {
    summary: string;
    tasks: Array<{
      name: string;
      description: string;
      departments: string[];
      estimatedHoursPerDepartment: Record<string, number>;
      priority: string;
    }>;
    totalEstimatedHours: number;
  };
}

export function Notes() {
  const { data: projects } = useApi<Project[]>("/api/projects");
  const [selectedProject, setSelectedProject] = useState<number>(0);
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [processing, setProcessing] = useState(false);
  const [result, setResult] = useState<ParsedResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async () => {
    if (!content.trim()) {
      setError("Please paste or type some notes");
      return;
    }
    if (!selectedProject) {
      setError("Please select a project");
      return;
    }

    setProcessing(true);
    setError(null);
    setResult(null);

    try {
      const res = await fetch("/api/notes/parse", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          content,
          title: title || undefined,
          projectId: selectedProject,
        }),
      });

      const data = await res.json();
      if (!res.ok) {
        setError(data.error || "Failed to parse notes");
      } else {
        setResult(data as ParsedResult);
      }
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setProcessing(false);
    }
  };

  const handleClear = () => {
    setTitle("");
    setContent("");
    setResult(null);
    setError(null);
  };

  return (
    <div>
      <div className="page-header">
        <h1>Notes</h1>
        <p>Paste notes to extract and generate tasks</p>
      </div>

      <div className="card">
        <div className="form-group">
          <label>Project</label>
          <SearchSelect
            options={projects?.map((p) => ({ value: p.id, label: p.name })) || []}
            value={selectedProject}
            onChange={setSelectedProject}
            placeholder="-- Select Project --"
          />
        </div>

        <div className="form-group">
          <label>Title (optional)</label>
          <input
            type="text"
            className="form-input"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="e.g. Client call notes, Sprint planning, etc."
          />
        </div>

        <div className="form-group">
          <label>Notes</label>
          <textarea
            className="form-input"
            value={content}
            onChange={(e) => setContent(e.target.value)}
            placeholder="Paste your notes here — meeting notes, client feedback, planning notes, task lists, etc."
            rows={12}
            style={{ resize: "vertical", fontFamily: "inherit" }}
          />
        </div>

        <div style={{ display: "flex", gap: "8px" }}>
          <button
            className="btn btn-primary"
            onClick={handleSubmit}
            disabled={processing || !content.trim() || !selectedProject}
          >
            {processing ? "Analyzing with AI..." : "Generate Tasks"}
          </button>
          {(content || title) && (
            <button className="btn btn-secondary" onClick={handleClear} disabled={processing}>
              Clear
            </button>
          )}
        </div>

        {error && (
          <div style={{ color: "var(--color-danger)", marginTop: "12px" }}>
            {error}
          </div>
        )}
      </div>

      {processing && (
        <LoadingOverlay message="Analyzing notes with AI... This may take a moment." />
      )}

      {result && (
        <div className="card">
          <div className="card-header">
            <h2>{result.parsed.summary || "Parsed Notes"}</h2>
            <span className="badge badge-info">
              {result.parsed.totalEstimatedHours}h total
            </span>
          </div>
          <table>
            <thead>
              <tr>
                <th>Task</th>
                <th>Departments</th>
                <th>Priority</th>
                <th>Hours</th>
              </tr>
            </thead>
            <tbody>
              {result.parsed.tasks.map((task, i) => (
                <tr key={i}>
                  <td>
                    <strong>{task.name}</strong>
                    <br />
                    <span style={{ color: "var(--color-text-muted)", fontSize: "13px" }}>
                      {task.description.slice(0, 100)}
                      {task.description.length > 100 ? "..." : ""}
                    </span>
                  </td>
                  <td>
                    {task.departments.map((d) => (
                      <span key={d} className="badge badge-neutral" style={{ marginRight: "4px" }}>
                        {d}
                      </span>
                    ))}
                  </td>
                  <td>
                    <span
                      className={`badge ${
                        task.priority === "high"
                          ? "badge-danger"
                          : task.priority === "medium"
                            ? "badge-warning"
                            : "badge-neutral"
                      }`}
                    >
                      {task.priority}
                    </span>
                  </td>
                  <td>
                    {Object.values(task.estimatedHoursPerDepartment).reduce(
                      (a, b) => a + b,
                      0
                    )}
                    h
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div style={{ marginTop: "16px", textAlign: "right" }}>
            <span style={{ color: "var(--color-text-muted)", marginRight: "12px" }}>
              Task Group ID: {result.taskGroupId}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}
